"use client";

import { memo, useMemo } from "react";
import { Box, HStack, VStack, Text, Flex } from "@chakra-ui/react";
import { getLikert5pAgreementOptions } from "@/shared/config/backend";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import { normalizeIconSize } from "@/shared/ui/actions/GenericIcon";
import type { ResponsiveIconSize } from "@/shared/ui/actions/GenericIcon";

interface ScaleInfoProps {
  direction?: "horizontal" | "vertical";
  showLabels?: boolean;
  size?: ResponsiveIconSize;
}

function ScaleInfoBase({
  direction = "horizontal",
  showLabels = true,
  size = "sm",
}: ScaleInfoProps) {
  const normalizedSize = normalizeIconSize(size);

  // ✅ Precompute Likert items (icons + labels) to avoid recreating elements each render
  const likertItems = useMemo(
    () =>
      getLikert5pAgreementOptions().map((item) => ({
        ...item,
      })),
    [],
  );

  // ✅ Vertical layout (icon + label per row)
  if (direction === "vertical") {
    return (
      <VStack spaceY={2} align="stretch" width="full">
        {likertItems.map((item) => (
          <HStack key={item.value} spaceX={3} align="center">
            <Box
              // @ts-ignore (Chakra color prop)
              color={item.color}
              flexShrink={0}
            >
              <GenericIcon
                icon={item.icon}
                humanReadable={item.label}
                size={normalizedSize}
              />
            </Box>
            {showLabels && <Text fontSize={size}>{item.label}</Text>}
          </HStack>
        ))}
      </VStack>
    );
  }

  // ✅ Horizontal layout (icons row + optional labels row)
  return (
    <VStack spaceY={2} align="stretch" width="full">
      {/* Icons Row */}
      <HStack justify="space-between" width="full" align="center">
        {likertItems.map((item) => (
          <Flex key={item.value} justify="center" align="center" flex="1">
            <Box
              // @ts-ignore -- Chakra accepts runtime color strings here.
              color={item.color}
            >
              <GenericIcon
                icon={item.icon}
                humanReadable={item.label}
                size={normalizedSize}
              />
            </Box>
          </Flex>
        ))}
      </HStack>

      {/* Labels Row */}
      {showLabels && (
        <HStack justify="space-between" width="full" align="start">
          {likertItems.map((item) => (
            <Text key={item.value} textAlign="center" flex="1" fontSize={size}>
              {item.label}
            </Text>
          ))}
        </HStack>
      )}
    </VStack>
  );
}

// ✅ Memoize to prevent unnecessary re-renders when parent updates
export default memo(ScaleInfoBase);
